import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { LayoutComponent } from 'bp/layout/layout.component';
import { AgentForensicsGuard } from 'projects/bp-admin/src/bp/@core/services/agent-forensics.guard';
import { AuthGuard } from 'projects/bp-admin/src/bp/@core/services/is-login.guard';
import { sudoGuard } from 'projects/bp-admin/src/bp/modules/oauth-clients/oauth-clients.guard';
import { AcceptedRiskGuard } from 'projects/bp-admin/src/bp/shared/guards/accepted-risk.guard';
import { sudoAccessGuard, sudoRootRedirect } from 'projects/bp-admin/src/bp/shared/guards/sudo-access.guard';
import { userNotificationsGuard } from 'projects/bp-admin/src/bp/shared/guards/user-notifications.guard';
import { HolidayAdvisorTypeEnum } from 'projects/bp-core/src/lib/proto/common-message.pb';
import { BpAuthGuard } from 'projects/bp-core/src/lib/services/portal/is-login.guard';
// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const bpAppRoutes: Route[] = [
  // Aterriza en la página de inicio de cada perfil (feature 057, FR-052), sin snackbar
  { path: '', redirectTo: sudoRootRedirect, pathMatch: 'full' },

  // NO guard - NO sidebar - NO bpstepsheader
  {
    path: '',
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      // Auth
      {
        path: 'auth',
        loadChildren: () => import('bp/modules/auth/auth.module').then(mod => mod.AuthModule),
      },

      // Maintenance
      {
        path: 'maintenance',
        loadChildren: () =>
          import('bp/modules/maintenance/maintenance.module').then(m => m.MaintenanceModule),
      },

      // Error pages
      {
        path: 'errors',
        children: [
          {
            path: '401',
            loadComponent: () =>
              import('bp/modules/error-pages/error401/error401.component').then(
                m => m.Error401Component,
              ),
          },
        ],
      },
    ],
  },

  // SÍ guard - si sidebar - NO bpstepsheader
  {
    path: '',
    canActivate: [BpAuthGuard, AcceptedRiskGuard],
    component: LayoutComponent,
    data: {
      layout: 'bp',
    },
    resolve: {
      initialData: initialDataResolver,
    },
    // Feature 057 (R-12): cada perfil solo abre las páginas de su menú (reemplaza al guard de la 055).
    // Las rutas nuevas por permiso (paneles de BT-7548) NO llevan `AuthGuard`: manda a la 401 a quien solo tiene
    // un rol de área. Los guards legados ceden el paso en la carga directa (`SudoAccessDeferral`, H4).
    canActivateChild: [sudoAccessGuard],
    children: [
      // Aviso para quien entra al sudo pero aún no tiene páginas habilitadas (sin AuthGuard: lo ve cualquiera
      // con acceso al sudo, y es el destino de quien no tiene ninguna otra página)
      {
        path: 'sin-modulos',
        loadComponent: () =>
          import('./modules/no-modules/no-modules.component').then(m => m.NoModulesComponent),
      },
      // Feature 064: paneles por área. Sin `AuthGuard` (manda a la 401 a quien solo tiene un rol de área):
      // el acceso lo decide `sudoAccessGuard` por el `meta.permission` del ítem del menú.
      {
        path: 'paneles/:panelKey',
        loadComponent: () =>
          import('./modules/sudo-panels/pages/panel-page/panel-page.component').then(
            m => m.PanelPageComponent,
          ),
      },
      // Feature 069: exportaciones del sudo. Sin `AuthGuard` (como los paneles): el acceso lo decide
      // `sudoAccessGuard` por el `meta.permission` del ítem del menú. `registro` va antes de `:id`.
      {
        path: 'exportaciones',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./modules/sudo-exports/pages/my-exports/my-exports.component').then(
                m => m.MyExportsComponent,
              ),
          },
          {
            path: 'registro',
            loadComponent: () =>
              import('./modules/sudo-exports/pages/export-audit/export-audit.component').then(
                m => m.ExportAuditComponent,
              ),
          },
          {
            path: 'registro/:eventId',
            loadComponent: () =>
              import(
                './modules/sudo-exports/pages/export-audit-detail/export-audit-detail.component'
              ).then(m => m.ExportAuditDetailComponent),
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./modules/sudo-exports/pages/export-detail/export-detail.component').then(
                m => m.ExportDetailComponent,
              ),
          },
        ],
      },
      //Dashboard
      {
        path: 'audit',
        canActivate: [AuthGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./modules/audit/audit-center/audit-center.component').then(
                c => c.AuditCenterComponent,
              ),
          },
          {
            path: 'events',
            loadChildren: () =>
              import('./modules/audit-events/audit-events.module').then(m => m.AuditEventsModule),
          },
          {
            path: 'entity/:serviceType/:entityId',
            loadComponent: () =>
              import('./modules/audit/audit-detail/audit-detail.component').then(
                c => c.AuditDetailComponent,
              ),
          },
        ],
      },
      {
        path: 'log-external',
        loadChildren: () =>
          import('./modules/log-external/log-external.module').then(m => m.LogExternalModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'integraciones/service-bus',
        loadChildren: () =>
          import('./modules/integraciones/service-bus/service-bus.routes').then(
            m => m.SERVICE_BUS_ROUTES,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./modules/dashboard/dashboard.module').then(m => m.DashboardModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'users-new',
        loadChildren: () => import('./modules/users-new/users.module').then(m => m.UsersModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'referral',
        loadChildren: () =>
          import('./modules/referral/referral.module').then(m => m.ReferralModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'bp-us',
        loadChildren: () => import('./modules/bp-us/bp-us.module').then(m => m.ReferralModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'questions',
        loadChildren: () =>
          import('./modules/questions/questions.module').then(m => m.QuestionsModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'plan-calculations',
        loadChildren: () =>
          import('./modules/plan-calculation/plan-calculation.module').then(
            m => m.PlanCalculationModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'goal-categories',
        loadChildren: () =>
          import('./modules/goal-category/goal-category.module').then(m => m.GoalCategoryModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'risk-profiles',
        loadChildren: () =>
          import('./modules/risk-profiles/profile.module').then(m => m.ProfileModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'financial-entities',
        loadChildren: () =>
          import('./modules/financial-entities/financial-entities.module').then(
            m => m.FinancialEntitiesModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'portfolios',
        loadChildren: () =>
          import('./modules/portfolio/portfolio.module').then(m => m.PortfolioModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'communes',
        loadChildren: () =>
          import('./modules/communes/communes.module').then(m => m.CommunesModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'regions',
        loadChildren: () => import('./modules/regions/regions.module').then(m => m.RegionsModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'tags',
        loadChildren: () => import('./modules/tags/tags.module').then(m => m.TagsModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'funds',
        loadChildren: () => import('./modules/funding/funding.module').then(m => m.FundingModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'funding-share-values',
        loadChildren: () =>
          import('./modules/funding-share-value/funding-share-value.module').then(
            m => m.FundingShareValueModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'composition-category',
        loadChildren: () =>
          import('./modules/composition-category/composition-category.module').then(
            m => m.CompositionCategoryModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'group-composition-category',
        loadChildren: () =>
          import('./modules/group-composition-category/group-composition-category.module').then(
            m => m.GroupCompositionCategoryModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'params',
        loadChildren: () => import('./modules/params/params.module').then(m => m.ParamsModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'box-transactions',
        loadChildren: () =>
          import('./modules/box-transactions/box-transactions.module').then(
            m => m.BoxTransactionsModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'contracts',
        loadChildren: () =>
          import('./modules/contracts/contracts.module').then(m => m.ContractsModule),
        canActivate: [AuthGuard],
      },
      // ECONSULT
      {
        path: 'econsult',
        loadChildren: () =>
          import('./modules/econsult/econsult.module').then(m => m.EconsultModule),
        canActivate: [AuthGuard],
      },
      // - ECONSULT

      // VECTOR
      {
        path: 'vector',
        loadChildren: () => import('./modules/vector/vector.module').then(m => m.VectorModule),
        canActivate: [AuthGuard],
      },
      // - VECTOR

      // BCI
      {
        path: 'bci',
        loadChildren: () => import('./modules/bci/bci.module').then(m => m.BciModule),
        canActivate: [AuthGuard],
      },
      // - BCI

      // BICE
      {
        path: 'bice',
        loadChildren: () => import('./modules/bice/bice.module').then(m => m.BiceModule),
        canActivate: [AuthGuard],
      },
      // - BICE

      {
        path: 'spread-ranges',
        loadChildren: () =>
          import('./modules/spread-ranges/spread-ranges.module').then(m => m.SpreadRangesModule),
        canActivate: [AuthGuard],
      },

      {
        path: 'config',
        loadChildren: () => import('./modules/config/config.module').then(m => m.ConfigModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'validation-ci',
        loadChildren: () =>
          import('./modules/validation-ci/validation-ci.module').then(m => m.ValidationCiModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'jobs',
        loadChildren: () =>
          import('./modules/jobs-vnext/jobs-vnext.module').then(m => m.JobsVnextModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'parseable-logs',
        loadComponent: () =>
          import('./modules/parseable-logs/parseable-logs.component').then(
            m => m.ParseableLogsComponent,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'otp-management',
        loadComponent: () =>
          import('./modules/otp-management/otp-management.component').then(
            m => m.OtpManagementComponent,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'risk-levels',
        loadChildren: () =>
          import('./modules/risk-levels/risk-levels.module').then(m => m.RiskLevelsModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'working-hours',
        loadChildren: () =>
          import('./modules/working-hours/working-hours.module').then(m => m.WorkingHoursModule),
        canActivate: [AuthGuard],
      },

      {
        path: 'advisor-vacations',
        loadChildren: () =>
          import('./modules/advisor-vacations/advisor-vacations.module').then(
            m => m.AdvisorVacationsModule,
          ),
        canActivate: [AuthGuard],
        data: { holidayAdvisorType: HolidayAdvisorTypeEnum.HOLIDAY_ADVISOR_TYPE_VACATION },
      },
      {
        // Mismo módulo que vacaciones, parametrizado por tipo (la ruta hija '' hereda esta data)
        path: 'advisor-replacements',
        loadChildren: () =>
          import('./modules/advisor-vacations/advisor-vacations.module').then(
            m => m.AdvisorVacationsModule,
          ),
        canActivate: [AuthGuard],
        data: { holidayAdvisorType: HolidayAdvisorTypeEnum.HOLIDAY_ADVISOR_TYPE_REPLACEMENT },
      },
      {
        path: 'holidays',
        loadChildren: () =>
          import('./modules/holidays/holidays.module').then(m => m.HolidaysModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'transactions',
        loadChildren: () =>
          import('./modules/transactions/transactions.module').then(m => m.TransactionsModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'transacciones',
        canActivate: [AuthGuard],
        children: [
          {
            path: 'comisiones-dividendos',
            loadComponent: () =>
              import('./modules/transacciones/comisiones-dividendos/comisiones-dividendos.component').then(
                m => m.ComisionesDividendosComponent,
              ),
          },
        ],
      },
      {
        path: 'transactions-funds',
        loadChildren: () =>
          import('./modules/goal-transaction-funding/goal-transaction-funding.module').then(
            m => m.GoalTransactionFundingModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'goal-transaction-errors',
        loadComponent: () =>
          import('./modules/goal-transaction-error/goal-transaction-error.component').then(
            m => m.GoalTransactionErrorComponent,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'time-horizon',
        loadChildren: () =>
          import('./modules/time-horizon/time-horizon.module').then(m => m.TimeHorizonModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'portfolio-calculation',
        loadChildren: () =>
          import('./modules/portfolio-calculation/portfolio-calculation.module').then(
            m => m.PortfolioCalculationModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'circularizations',
        loadChildren: () =>
          import('./modules/circularization/circularization.module').then(
            m => m.CircularizationModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'risk-profile-responses',
        loadChildren: () =>
          import('./modules/risk-profile-responses/risk-profile-responses.module').then(
            m => m.RiskProfileResponsesModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'investment-strategy',
        loadChildren: () =>
          import('./modules/investment-strategy/investment-strategy.module').then(
            m => m.InvestmentStrategyModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'investment-strategy-type',
        loadChildren: () =>
          import('./modules/investment-strategy-type/investment-strategy-type.module').then(
            m => m.InvestmentStrategyTypeModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'bank-transactions',
        loadChildren: () =>
          import('./modules/bank-transaction/bank-transaction.module').then(
            m => m.BankTransactionModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'advisor-assignment-rules',
        loadChildren: () =>
          import('./modules/advisor-assignment-rule/advisor-assignment-rule.module').then(
            m => m.AdvisorAssignmentRuleModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'currency',
        loadChildren: () =>
          import('./modules/currency/currency.module').then(m => m.CurrencyModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'bank',
        loadChildren: () => import('./modules/bank/bank.routes').then(m => m.bankRoutes),
        canActivate: [AuthGuard],
      },
      {
        path: 'roles',
        loadChildren: () => import('./modules/role/role.module').then(m => m.RoleModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'oauth-clients',
        canActivate: [sudoGuard],
        loadChildren: () =>
          import('./modules/oauth-clients/oauth-clients.routes').then(
            m => m.OAUTH_CLIENTS_ROUTES,
          ),
      },
      {
        path: 'transaction-requests',
        loadChildren: () =>
          import('./modules/transaction-requests/transaction-requests.module').then(
            m => m.TransactionRequestsModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'external',
        loadChildren: () =>
          import('./modules/external/external.module').then(m => m.ExternalModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'internal-notification-event-configuration',
        loadChildren: () =>
          import(
            './modules/internal-notification-event-configuration/internal-notification-event-configuration.module'
          ).then(m => m.InternalNotificationEventConfigurationModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'templates',
        loadChildren: () =>
          import('./modules/templates/templates.module').then(m => m.TemplatesModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'products',
        loadComponent: () =>
          import('./modules/products/products.component').then(m => m.ProductsComponent),
        canActivate: [AuthGuard],
      },
      {
        path: 'dps-conditions',
        loadComponent: () =>
          import('./modules/products/dps-conditions/dps-conditions.component').then(
            m => m.DpsConditionsComponent,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'product-types',
        loadComponent: () =>
          import('./modules/product-types/product-types.component').then(
            m => m.ProductTypesComponent,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'product-plans',
        loadComponent: () =>
          import('./modules/product-plans/product-plans.component').then(
            m => m.ProductPlansComponent,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'potencial-plan',
        loadChildren: () =>
          import('./modules/potencial-plan/potencial-plan.routes').then(m => m.routes),
        canActivate: [AuthGuard],
      },
      {
        path: 'user-notifications',
        loadChildren: () =>
          import('./modules/user-notifications/user-notifications.module').then(
            m => m.UserNotificationsModule,
          ),
        // Feature 055 (R-12): admin, sudo o customer-success; sin AuthGuard para no crear un bucle.
        canActivate: [userNotificationsGuard],
      },
      {
        path: 'apps/whatsapp',
        loadChildren: () => import('./modules/whatsapp/whatsapp.routes').then(m => m.default),
        canActivate: [AuthGuard],
      },
      {
        path: 'finanzas',
        pathMatch: 'full',
        redirectTo: 'finanzas/utilidades',
      },
      {
        path: 'finanzas/utilidades',
        loadComponent: () =>
          import('./modules/finanzas/finanzas-list/finanzas-list.component').then(
            m => m.FinanzasListComponent,
          ),
        canActivate: [AuthGuard],
      },
      // MARKET DATA — feed de precios en tiempo real
      {
        path: 'market-data/live-prices',
        loadComponent: () =>
          import('./modules/market-data/pages/live-prices/live-prices.component').then(
            m => m.LivePricesComponent,
          ),
        canActivate: [AuthGuard],
      },
      // - MARKET DATA
      // REDIS EXPLORER — visor read-only de la instancia Redis (spec 029, solo rol `sudo`)
      {
        path: 'redis-explorer',
        canActivate: [AuthGuard, sudoGuard],
        loadComponent: () =>
          import('./modules/redis-explorer/pages/redis-explorer/redis-explorer.component').then(
            m => m.RedisExplorerComponent,
          ),
      },
      // - REDIS EXPLORER
      {
        path: 'finanzas/comisiones-vector-ibkr',
        loadComponent: () =>
          import(
            './modules/finanzas/utilidades/comisiones-vector-ibkr/comisiones-vector-ibkr.component'
          ).then(m => m.ComisionesVectorIbkrComponent),
        canActivate: [AuthGuard],
      },
      {
        path: 'finanzas/inyeccion-rebates',
        loadComponent: () =>
          import('./modules/rebate-injection/rebate-injection.component').then(
            m => m.RebateInjectionComponent,
          ),
        canActivate: [AuthGuard],
      },
      // NPS — reporte de la encuesta in-app (BT-6796, FR-019/FR-020)
      {
        path: 'reportes/nps',
        loadComponent: () =>
          import('./modules/nps-report/nps-report.component').then(m => m.NpsReportComponent),
        canActivate: [AuthGuard],
      },
      {
        path: 'finanzas/reporte-com-trs',
        loadComponent: () =>
          import('./modules/finanzas/utilidades/reporte-com-trs/reporte-com-trs.component').then(
            m => m.ReporteComTrsComponent,
          ),
        canActivate: [AuthGuard],
      },
      // ALPACA
      {
        path: 'alpaca',
        canActivate: [AuthGuard],
        children: [
          {
            path: 'api',
            loadComponent: () =>
              import('./modules/alpaca/pages/alpaca-api/alpaca-api.component').then(
                c => c.AlpacaApiComponent,
              ),
          },
        ],
      },
      // - ALPACA
      // VECTOR API
      {
        path: 'vector-api',
        loadComponent: () =>
          import('./modules/apis/pages/vector-api/vector-api.component').then(
            c => c.VectorApiComponent,
          ),
        canActivate: [AuthGuard],
      },
      // - VECTOR API
      // Asesor IA del sudo (049, T095; HU6): según GetAvailability; un id entero es un enlace del sistema anterior, que
      // se resuelve a su conversación migrada o abre una nueva con aviso
      {
        path: 'ai-chat',
        loadComponent: () =>
          import('./modules/ai-chat/pages/sudo-ai-chat-route.component').then(
            m => m.SudoAiChatRouteComponent,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'ai-chat/:chatId',
        loadComponent: () =>
          import('./modules/ai-chat/pages/sudo-ai-chat-route.component').then(
            m => m.SudoAiChatRouteComponent,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: 'mcp-guide',
        loadComponent: () =>
          import('./modules/mcp-guide/mcp-guide.component').then(m => m.McpGuideComponent),
        canActivate: [AuthGuard],
      },
      {
        path: 'ia',
        // ia-manager o sudo (sudo solo llega a la forense: cada hija lleva su propio guard).
        canActivate: [AgentForensicsGuard],
        loadChildren: () =>
          import('./modules/ai-admin/ai-admin.routes').then(m => m.AI_ADMIN_ROUTES),
      },
      { path: '', redirectTo: '/dashboard', pathMatch: 'full' },
    ],
  },
];

export const notMatchRedirectRoutesOld: Route[] = [
  {
    path: 'not-found',
    pathMatch: 'full',
    loadChildren: () => import('bp/modules/error-404/error-404.routes'),
  },
  { path: '**', redirectTo: 'not-found' },
];

//{ path: '**', redirectTo: 'redirect-to', pathMatch: 'full' },
//{ path: '**', redirectTo: '404-not-found' },
